/**
 * Cart API Interceptor
 *
 * Intercepts fetch calls to Shopify cart endpoints made by third-party apps
 * (e.g., MoonBundle, bundle apps) and triggers the theme's internal cart
 * update event so the cart drawer and cart page refresh automatically.
 *
 * This solves the problem where external apps modify the cart but our theme
 * never knows about it because they don't use our internal pub/sub system.
 */
(function () {
  if (window.__cartApiInterceptorInstalled) return;
  window.__cartApiInterceptorInstalled = true;

  const CART_ENDPOINTS = [
    '/cart/add.js',
    '/cart/add',
    '/cart/change.js',
    '/cart/change',
    '/cart/update.js',
    '/cart/update',
    '/cart/clear.js',
    '/cart/clear',
  ];

  // Debounce to avoid multiple rapid refreshes
  let refreshTimer = null;
  const REFRESH_DEBOUNCE = 300;

  function isCartEndpoint(url) {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      return CART_ENDPOINTS.some(function (endpoint) {
        return pathname === endpoint || pathname.endsWith(endpoint);
      });
    } catch (e) {
      return typeof url === 'string' && CART_ENDPOINTS.some(function (endpoint) {
        return url.includes(endpoint);
      });
    }
  }

  function scheduleCartRefresh(source) {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshTimer = null;

      // Publish to internal pub/sub if available
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: source || 'third-party-app',
        });
      }

      // Also refresh cart drawer directly as fallback
      refreshCartDrawer();
      refreshCartIconBubble();
    }, REFRESH_DEBOUNCE);
  }

  function refreshCartDrawer() {
    var cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer) return;

    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    fetch(cartUrl + '?section_id=cart-drawer')
      .then(function (response) { return response.text(); })
      .then(function (responseText) {
        var html = new DOMParser().parseFromString(responseText, 'text/html');
        var source = html.querySelector('#CartDrawer');
        if (source) {
          cartDrawer.innerHTML = source.innerHTML;
        }
      })
      .catch(function () {});
  }

  function refreshCartIconBubble() {
    var bubble = document.querySelector('.cart-count-bubble');
    if (!bubble) return;

    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    fetch(cartUrl + '?section_id=cart-icon-bubble')
      .then(function (response) { return response.text(); })
      .then(function (responseText) {
        var html = new DOMParser().parseFromString(responseText, 'text/html');
        var source = html.querySelector('.cart-count-bubble');
        if (source) {
          bubble.innerHTML = source.innerHTML;
        }
      })
      .catch(function () {});
  }

  // Track which calls originate from our own theme code
  var isInternalCall = false;

  // Expose a way for theme code to mark its own calls
  window.__markInternalCartCall = function () { isInternalCall = true; };
  window.__unmarkInternalCartCall = function () { isInternalCall = false; };

  // Intercept fetch
  var originalFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
    var method = (init && init.method) || (input && input.method) || 'GET';
    var isCart = method.toUpperCase() !== 'GET' && isCartEndpoint(url);

    // Skip if this is an internal theme call (already handled by pub/sub)
    if (isCart && isInternalCall) {
      isInternalCall = false;
      return originalFetch.apply(this, arguments);
    }

    if (isCart) {
      return originalFetch.apply(this, arguments).then(function (response) {
        // Clone so the original consumer can still read the response
        var cloned = response.clone();
        cloned.json().catch(function () { return null; }).then(function () {
          scheduleCartRefresh('third-party-app');
        });
        return response;
      });
    }

    return originalFetch.apply(this, arguments);
  };

  // Intercept XMLHttpRequest for apps that use XHR instead of fetch
  var originalXHROpen = XMLHttpRequest.prototype.open;
  var originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__cartInterceptUrl = url;
    this.__cartInterceptMethod = method;
    return originalXHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    var xhr = this;
    var url = xhr.__cartInterceptUrl || '';
    var method = (xhr.__cartInterceptMethod || 'GET').toUpperCase();

    if (method !== 'GET' && isCartEndpoint(url) && !isInternalCall) {
      xhr.addEventListener('load', function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          scheduleCartRefresh('third-party-app-xhr');
        }
      });
    }

    isInternalCall = false;
    return originalXHRSend.apply(this, arguments);
  };
})();
